/**
 * App
 * ---
 * 
 * October 2026
 */

import "./App.css";
import AnalyticsPanel from "./Analytics/AnalyticsPanel.js";
import DOEAnalyzerPage from "./DOEAnalyzer/DOEAnalyzerPage.js";
import AISettingsPage from "./Settings/AISettingsPage.js";
import ConfigPanel from "./configuration/ConfigPanel.js";
import SearchBar from "./SearchBar.js";
import AssistantBubble from "./Assistant/AssistantBubble.js";
import { summarizeExperimentSetup, summarizeModel } from "./Assistant/modelSummary.js";

import * as React from "react";

import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { Button } from "primereact/button";
import { FileUpload } from "primereact/fileupload";
import { Dropdown } from "primereact/dropdown";
import { Accordion, AccordionTab } from "primereact/accordion";
import { Toast } from "primereact/toast";

import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { redirect } from "react-router-dom";
import axios from "axios";

import { useKeycloak } from "@react-keycloak/web";
axios.defaults.withCredentials = true;

/**
 * Builds a compact, page-appropriate context summary to send with every
 * assistant chat turn, so replies can be grounded in what the user is
 * currently looking at. Kept intentionally light on the Analytics/DOE
 * Analyzer routes -- those pages already have their own dedicated AI
 * features, so the floating assistant there is more general-purpose.
 *
 * @param {String} pathname   - The current route.
 * @param {Object} config     - The app's configuration state.
 * @param {Object} doeSummary - The DOE Analyzer's current results, if any.
 * @param {Object} experimentSetup - The experiment panel's selections, if any.
 */
function buildAssistantContext(pathname, config, doeSummary, experimentSetup) {
  if (pathname === "/") {
    return {
      page: "model_setup",
      model_name: config.model_name,
      entity_type_count: Object.keys(config.entity_types || {}).length,
      action_count: Object.keys(config.actions || {}).length,
      resource_count: Object.keys(config.resources || {}).length,
      system: config.system,
      ...summarizeModel(config),
      experiment: summarizeExperimentSetup(config, experimentSetup),
    };
  }
  // Off the setup page, the model loaded there may not be the one being looked at.
  if (pathname === "/Analytics") {
    return { page: "analytics", model_loaded_on_setup_page: config.model_name };
  }
  if (pathname === "/DOEAnalyzer") {
    return {
      page: "doe_analyzer",
      ...(doeSummary || { analysis: "No experiment has been analyzed on this page yet." }),
      model_loaded_on_setup_page: config.model_name,
    };
  }
  return { page: pathname };
}

/**
 * DESMA's GUI for building, configuring, & running simulation models.
 */
function App() {
  const { keycloak, initialized } = useKeycloak();
  const [use_keycloak, set_use_keycloak] = React.useState(
    process.env.REACT_APP_KEYCLOAK_USER ? false : true,
  );

  const navigate = useNavigate();
  const location = useLocation();
  // The DOE Analyzer's results, so the AI Assistant can answer questions about them.
  const [doeSummary, setDoeSummary] = React.useState(null);
  // The experiment panel's selections, so the AI Assistant can answer about them.
  const [experimentSetup, setExperimentSetup] = React.useState(null);

  // References the stepper component.
  const stepperRef = React.useRef(null);

  // Organization projects retrieved from Innoslate.
  const [projects, setProjects] = React.useState([]);

  // Manages the selected project to parse.
  const [selectedProject, setSelectedProject] = React.useState(null);

  // Organization projects retrieved from Innoslate.
  const [diagrams, setDiagrams] = React.useState([]);

  // Manages the selected diagram to parse.
  const [selectedDiagram, setSelectedDiagram] = React.useState(null);

  const [previousModels, setPreviousModels] = React.useState([]);

  const [selectedPreviousModel, setSelectedPreviousModel] = React.useState(null);

  const [innoslateConnected, setInnoslateConnected] = React.useState(null);

  const [entryMode, setEntryMode] = React.useState(null);

  // Manages the configuration parameters.
  const [config, setConfig] = React.useState({
    system: {
      trials: 1,
      duration: 10,
      time_unit: "seconds",
      time_interval: 5,
      honor_only_first: true,
      honor_only_highest_priority: true,
      resource_arrivals: {
        enabled: false,
        script: []
      }
    },
    entity_types: {},
    actions: {},
    resources: {},
    experiment: { name: "DES", type: "None", input: [] },
    model_name: "",
  });

  // Manages the current entity type being rendered.
  const [selectedEntityType, setEntityType] = React.useState(null);

  // Manages the current action element being rendered.
  const [selectedAction, setAction] = React.useState(null);

  //const [selectedModel, setModelName] = React.useState(null);

  // Manages the current resource element being rendered.
  const [selectedResource, setResource] = React.useState(null);

  // Manages warnings to display for the configuration parameters.
  const [configWarnings, setConfigWarnings] = React.useState({
    system: {},
    entity_types: {},
    resources: {},
    actions: {},
    experimentation: {},
  });

  // Checks when users have reached the configuration panel.
  const [reachedConfig, setReachedConfig] = React.useState(0);

  const [experimentLoading, setExperimentLoading] = React.useState(false);

  // Progress of the currently-running experiment job, polled from
  // /api/experiment/job/<job_id>/status.
  const [experimentProgress, setExperimentProgress] = React.useState({
    completed: 0,
    total: 0,
  });

  // Tracks the poll timer so a stale poll can be cancelled if the
  // component unmounts or a new experiment starts mid-poll.
  const pollTimeoutRef = React.useRef(null);

  // The running job's id, for the Cancel button.
  const [currentJobId, setCurrentJobId] = React.useState(null);

  // Results behind diagram simulation.
  const [experiment_ids, setResults] = React.useState([]);

  const [trial_summaries, setTrialsSummary] = React.useState({
    trials: [],
    total_time: [],
    experiment_name: [],
    x: [],
  });

  const [experiment_summaries, setExperiment] = React.useState({
    name: [],
    run_time_avg: [],
    _id: [],
    model_name: [],
  });

  /**
   * Sends an API post request to parse the file contents.
   * Retrieves parsed attributes from DESMA's server.
   *
   * @param {Object} event  - File being uploaded.
   */
  const parseFile = (event) => {
    console.log("Parsing Innoslate file");
    setEntryMode("direct");

    const xmlFile = event.files[0];
    const formData = new FormData();
    formData.append("file", xmlFile);

    axios
      .post(`/api/upload`, formData)
      .then((res) => {
        console.log(res.data);

        // Initialize state for configuration parameters.
        const modelName = res.data.model_name;
        const initialActions = res.data.actions;
        const initialResources = res.data.resources;
        const entityTypeGID = crypto.randomUUID();
        const initialDist = { dist: "constant", attr: { value: 1 } };
        const initialEntityTypes = {
          [entityTypeGID]: {
            gid: entityTypeGID,
            name: "Entity",
            entities_per_arrival: initialDist,
            interarrival: initialDist,
            capacity: { is_enabled: false, value: null },
          },
        };
        const newConfig = {
          ...config,
          actions: initialActions,
          resources: initialResources,
          entity_types: initialEntityTypes,
          model_name: modelName,
        };

        console.log(newConfig);
        setEntityType(Object.keys(initialEntityTypes)[0]);
        setAction(Object.keys(initialActions)[0]);
        setResource(Object.keys(initialResources)[0]);
        setConfig(newConfig);
        setReachedConfig((preState) => preState + 1);
      })
      .catch((err) => {
        console.error(err);
      });

    stepperRef.current.nextCallback();
  };

  /**
   * Loads a model the user previously uploaded
   *
   * @param {string} filename  - Name of the previously uploaded file.
   */
  const loadPreviousModel = (filename) => {
    console.log("Loading previously uploaded model: " + filename);
    setEntryMode("direct");

    axios
      .get(`/api/models/${encodeURIComponent(filename)}`)
      .then((res) => {
        console.log(res.data);

        const modelName = res.data.model_name;
        const initialActions = res.data.actions;
        const initialResources = res.data.resources;
        const entityTypeGID = crypto.randomUUID();
        const initialDist = { dist: "constant", attr: { value: 1 } };
        const initialEntityTypes = {
          [entityTypeGID]: {
            gid: entityTypeGID,
            name: "Entity",
            entities_per_arrival: initialDist,
            interarrival: initialDist,
            capacity: { is_enabled: false, value: null },
          },
        };
        const newConfig = {
          ...config,
          actions: initialActions,
          resources: initialResources,
          entity_types: initialEntityTypes,
          model_name: modelName,
        };
        setEntityType(Object.keys(initialEntityTypes)[0]);
        setAction(Object.keys(initialActions)[0]);
        setResource(Object.keys(initialResources)[0]);
        setConfig(newConfig);
        setReachedConfig((preState) => preState + 1);
      })
      .catch((err) => {
        console.error(err);
      });

    stepperRef.current.nextCallback();
  };

  // Makes an API get request to retrieve org projects from the backend server.
  // Moves to next stepper panel.
  const loadProjects = () => {
    console.log("Loading projects");
    setEntryMode("innoslate");

    axios
      .get(`/api/projects`)
      .then((res) => {
        console.log("Projects: ", res.data);
        setProjects(res.data);
      })
      .catch((err) => {
        console.error(err);
        setProjects([]);
      });

    stepperRef.current.nextCallback();
  };

  // Makes an API get request to retrieve org projects from the backend server.
  // Moves to next stepper panel.
  const loadDiagrams = (id) => {
    console.log("Loading diagrams");

    axios
      .get(`/api/diagrams?project_id=${id}`)
      .then((res) => {
        console.log(res.data);
        setDiagrams(res.data);
      })
      .catch((err) => {
        console.error(err);
        setDiagrams([]);
      });

    stepperRef.current.nextCallback();
  };

  // Makes an API post request to signal the backend server to parse diagram components.
  // Moves to next stepper panel.
  const parseDiagram = (globalId) => {
    console.log("Parsing chosen diagram");

    axios
      .post(`/api/parseDiagram`, { diagramId: globalId })
      .then((res) => {
        // Initialize state for configuration parameters.
        const modelName = res.data.model_name;
        const initialActions = res.data.actions;
        const initialResources = res.data.resources;
        const entityTypeGID = crypto.randomUUID();
        const initialDist = { dist: "constant", attr: { value: 1 } };
        const initialEntityTypes = {
          [entityTypeGID]: {
            gid: entityTypeGID,
            name: "Entity",
            entities_per_arrival: initialDist,
            interarrival: initialDist,
            capacity: { is_enabled: false, value: null },
          },
        };
        const newConfig = {
          ...config,
          actions: initialActions,
          resources: initialResources,
          entity_types: initialEntityTypes,
          model_name: modelName,
        };
        setEntityType(Object.keys(initialEntityTypes)[0]);
        setAction(Object.keys(initialActions)[0]);
        setResource(Object.keys(initialResources)[0]);
        setConfig(newConfig);
        setReachedConfig((preState) => preState + 1);
      })
      .catch((err) => {
        console.error(err);
      });

    stepperRef.current.nextCallback();
  };

  // Polls /api/experiment/job/<job_id>/status until the background
  // experiment run finishes (or errors), updating experimentProgress along
  // the way.
  const pollExperimentJob = (jobId) => {
    axios
      .get(`/api/experiment/job/${jobId}/status`)
      .then((res) => {
        const { status, completed_trials, total_trials } = res.data;
        setExperimentProgress({
          completed: completed_trials || 0,
          total: total_trials || 0,
        });

        if (status === "complete") {
          const result = res.data.result || {};
          console.log("Experiment complete");
          console.log(result);
          setResults(result["experiment_ids"]);
          setTrialsSummary(result["trials"]);
          setExperiment(result["experiments"]);
          setExperimentLoading(false);
          setCurrentJobId(null);
          navigate("/Analytics");
        } else if (status === "error") {
          console.error("Experiment job failed:", res.data.error);
          setExperimentLoading(false);
          setCurrentJobId(null);
        } else if (status === "cancelled") {
          console.log("Experiment job cancelled.");
          // A cancelled run's partial results aren't kept. DELETE, not GET: GET downloads them.
          if (res.data.experiment_id) {
            fetch(`/ana/db/${res.data.experiment_id}`, {
              method: "DELETE",
              headers: { "connect-key": process.env.REACT_APP_GRAFANA_API_KEY },
            }).catch((err) => console.error("Failed to delete the cancelled experiment:", err));
          }
          setExperimentLoading(false);
          setCurrentJobId(null);
        } else {
          // Still running (or cancelling) -- poll again shortly.
          pollTimeoutRef.current = setTimeout(() => pollExperimentJob(jobId), 2000);
        }
      })
      .catch((err) => {
        console.error("Failed to poll experiment job status:", err);
        setExperimentLoading(false);
        setCurrentJobId(null);
      });
  };

  // Requests cancellation of the running job; it stops after the trial in progress.
  const cancelRun = () => {
    if (!currentJobId) {
      return;
    }
    axios
      .post(`/api/experiment/job/${currentJobId}/cancel`)
      .catch((err) => {
        console.error("Failed to cancel job:", err);
      });
  };

  // Makes an API post request to run a DES simulation.
  const simulateDiagram = (verifyConfig, createExperimentConfig) => {
    console.log("Verifying user's configuration parameters.");
    const isValid = verifyConfig();
    if (!isValid) {
      console.log(
        "Invalid configuration. Please review warnings & adjust parameters.",
      );
      return;
    }

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
    }
    setExperimentLoading(true);
    setExperimentProgress({ completed: 0, total: 0 });

    if (config.experiment.type !== "None") {
      console.log("Creating experiment configuration...");
      const experimentConfig = {
        ...config,
        experiment: { ...config.experiment, input: createExperimentConfig() },
      };
      console.log("Experiment Config (App.js)", experimentConfig.experiment);

      // Kicks off the experiment on the backend and gets a job_id back
      // immediately -- the run itself happens on a background thread
      // there, tracked by polling below.
      console.log("Running experiment...");
      axios
        .post(`/api/simulateExperiment`, { config: experimentConfig })
        .then((res) => {
          const jobId = res.data.job_id;
          if (!jobId) {
            console.error("No job_id in response:", res.data);
            setExperimentLoading(false);
            return;
          }
          setCurrentJobId(jobId);
          pollExperimentJob(jobId);
        })
        .catch((err) => {
          console.error(err);
          setExperimentLoading(false);
        });
    } else {
      console.log("Simulation configuration: ", config);
      console.log("Running DES simulation...");

      axios
        .post(`/api/simulate`, { config: config })
        .then((res) => {
          const jobId = res.data.job_id;
          if (!jobId) {
            console.error("No job_id in response:", res.data);
            setExperimentLoading(false);
            return;
          }
          setCurrentJobId(jobId);
          pollExperimentJob(jobId);
        })
        .catch((err) => {
          console.error(err);
          setExperimentLoading(false);
        });
    }
  };

  React.useEffect(() => {
    return () => {
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, []);

  //assigns the username


  // Checks on mount whether this deployment's backend can reach Innoslate.
  React.useEffect(() => {
    axios
      .get(`/api/innoslate_status`)
      .then((res) => {
        setInnoslateConnected(res.data.connected);
      })
      .catch((err) => {
        console.error(err);
        setInnoslateConnected(false);
      });
  }, []);

  // Fetches the current user's previously uploaded models on mount.
  React.useEffect(() => {
    axios
      .get(`/api/models`)
      .then((res) => {
        setPreviousModels(res.data);
      })
      .catch((err) => {
        console.error(err);
      });
  }, []);

  // Manages the parsing/simulating process for an action diagram.
  // Make Rest API requests to communicate with the project's backend server.
  const DiagramParser = () => {
    const stepperPanels = [
      <StepperPanel key="welcome" header="Welcome">
        <h4
          style={{
            marginBottom: "0.25rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          Use a model from Innoslate
          {innoslateConnected === true && (
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: "normal",
                color: "green",
              }}
            >
              ● Connected
            </span>
          )}
          {innoslateConnected === false && (
            <span
              style={{
                fontSize: "0.75rem",
                fontWeight: "normal",
                color: "red",
              }}
            >
              ● Not connected
            </span>
          )}
        </h4>
        <p style={{ marginTop: 0 }}>
          Browse action diagrams already in your organization's Innoslate
          project.
        </p>
        <Button
          label="Browse Innoslate Projects"
          icon="pi pi-arrow-right"
          iconPos="right"
          onClick={loadProjects}
          disabled={innoslateConnected === false}
        />

        <h4 style={{ marginBottom: "0.25rem", marginTop: "1.5rem" }}>
          Upload your own model file
        </h4>
        <p style={{ marginTop: 0 }}>
          Upload an action diagram you've exported from Innoslate as an XML
          or JSON file.
        </p>
        <FileUpload
          mode="basic"
          customUpload
          auto
          uploadHandler={parseFile}
          chooseLabel="Upload XML/JSON File"
          accept=".xml,.json"
          maxFileSize={1000000}
        />

        {previousModels.length > 0 && (
          <>
            <h4 style={{ marginBottom: "0.25rem", marginTop: "1.5rem" }}>
              Reload a previously uploaded model
            </h4>
            <p style={{ marginTop: 0 }}>
              Pick a model file you've uploaded before.
            </p>
            <Dropdown
              value={selectedPreviousModel}
              options={previousModels}
              onChange={(event) => {
                setSelectedPreviousModel(event.value);
                loadPreviousModel(event.value);
              }}
              placeholder="Select a model"
            />
          </>
        )}
      </StepperPanel>,
    ];

    if (entryMode === "innoslate") {
      stepperPanels.push(
        <StepperPanel key="project" header="Choose Project">
          <SearchBar
            items={projects}
            selectedItem={selectedProject}
            setSelectedItem={setSelectedProject}
            label="Project"
            endpoint={loadDiagrams}
          />
        </StepperPanel>,
        <StepperPanel key="diagram" header="Choose Diagram">
          <SearchBar
            items={diagrams}
            selectedItem={selectedDiagram}
            setSelectedItem={setSelectedDiagram}
            label="Diagram"
            endpoint={parseDiagram}
          />
        </StepperPanel>,
      );
    }

    stepperPanels.push(
      <StepperPanel key="configure" header="Configure & Run">
        <ConfigPanel
          configState={{
            config,
            setConfig,
            configWarnings,
            setConfigWarnings,
            reachedConfig,
            setReachedConfig,
          }}
          elementState={{
            selectedEntityType,
            setEntityType,
            selectedAction,
            setAction,
            selectedResource,
            setResource,
          }}
          simulateDiagram={simulateDiagram}
          isLoading={experimentLoading}
          progress={experimentProgress}
          cancelRun={cancelRun}
          onExperimentSetupChange={setExperimentSetup}
        />
      </StepperPanel>,
    );

    return (
      <div>
        <p>
          Welcome, {use_keycloak ? keycloak.tokenParsed?.preferred_username:process.env.REACT_APP_KEYCLOAK_USER}
          <button onClick={() => keycloak.logout()}>Logout</button>
        </p>
        <Stepper linear ref={stepperRef}>
          {stepperPanels}
        </Stepper>
      </div>
    );
  };
  if (use_keycloak && !initialized) {
    return <div>Loading …</div>;
  }
  if (use_keycloak && keycloak.authenticated){
    if(!keycloak.hasRealmRole("verified")){
      return (
      <div className="App">
        <h2>Your user account has not been verified.<br/>  Please try again later or contact System_Administrator@ThinkSTS.com</h2>
        <button className="m-1" onClick={() => keycloak.logout()}>Logout</button>
      </div>);
    }
  }
  return (
    <div className="App">
      {!use_keycloak || keycloak.authenticated ? (
        <div>
          <nav>
            <ul>
              <li>
                <Link to="/">Simulation & Experiments</Link>
              </li>
              <li>
                <Link to="/Analytics">Analytics</Link>
              </li>
              <li>
                <Link to="/DOEAnalyzer">DOE Analyzer</Link>
              </li>
              <li>
                <Link to="/AISettings">AI Settings</Link>
              </li>
              <li>
                <a href="/grafana/" target="_blank" rel="noopener noreferrer">
                  Grafana
                </a>
              </li>
              <li>
                <a href="/docs/" target="_blank" rel="noopener noreferrer">
                  Docs
                </a>
              </li>
            </ul>
          </nav>
          <Routes>
            <Route path="/" element={DiagramParser()}></Route>
            <Route
              path="/Analytics"
              element={
                <AnalyticsPanel
                  experiment_ids={experiment_ids}
                  trial_summaries={trial_summaries}
                  experiment_summaries={experiment_summaries}
                />
              }
            ></Route>
            <Route
              path="/DOEAnalyzer"
              element={<DOEAnalyzerPage experiment_ids={experiment_ids} onSummaryChange={setDoeSummary} />}
            ></Route>
            <Route path="/AISettings" element={<AISettingsPage />}></Route>
          </Routes>
          <AssistantBubble context={buildAssistantContext(location.pathname, config, doeSummary, experimentSetup)}/>
        </div>
      ) : (
        keycloak.login()
      )}
    </div>
  );
}

export default App;
